import React from 'react'
import { useTranslation } from 'react-i18next'
import { MapContainer, TileLayer, ImageOverlay } from 'react-leaflet'

import 'leaflet.sync'
import L from 'leaflet'
import { find, flatMap } from 'lodash'
import { makeStyles } from '@material-ui/core/styles'
import withWidth, { isWidthUp } from '@material-ui/core/withWidth'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import VisibilityIcon from '@material-ui/icons/Visibility'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff'

import { computeLatLngBoundsFromGeojson } from '../../../../utils/map'
import { EditParcelGeoman } from './EditParcelGeoman'
import { TotalHectaresControl } from './TotalHectaresControl'
import { useAuth } from '@graniot/common/context/AuthContext'
import Parcels from '@graniot/common/components/map/Parcels'
import MapLayersManager from '@graniot/common/components/map/MapLayersManager'
import Legend from '@graniot/common/components/map/Legend'
import {
  usePaletteLegend,
  usePaletteScript,
  withPaletteScript,
} from '../../../../adapters/paletteRange'
import { usePaletteAdjustmentsEnabled } from '../../../../context/PaletteAdjustmentsContext'

// Center map on Granada
const position = [37.741934, -3.188515]

const useStyles = makeStyles((theme) => ({
  wrapper: {
    position: 'relative',
  },
  loading: {
    marginBottom: theme.spacing(20),
    marginTop: theme.spacing(20),
  },
  indexLayerToggle: {
    position: 'absolute',
    zIndex: 1000,
    top: 60,
    right: 12,
    width: 36,
    height: 36,
    backgroundColor: '#fff',
    border: '1.5px solid rgba(0,0,0,0.1)',
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
    borderRadius: 12,
    color: '#9CA3AF',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#fff',
      color: '#232C57',
      boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
      borderColor: 'rgba(35, 44, 87, 0.25)',
    },
  },
  indexLayerToggleActive: {
    backgroundColor: '#232C57',
    color: '#fff',
    borderColor: '#232C57',
    boxShadow: '0 2px 8px rgba(35, 44, 87, 0.4)',
    '&:hover': {
      backgroundColor: '#1a2145',
      color: '#fff',
      borderColor: '#1a2145',
    },
  },
  toggleIcon: {
    fontSize: 19,
  },
}))

function EditParcelMap({
  farms,
  parcel,
  layers,
  dispatchAddedParcels,
  parcelGeoJson,
  setParcelGeoJson,
  editParcel,
  setUpdateButtonDisabled,
  selectedResolution,
  layer,
  selectedLayerDate,
  farmsHectares,
  showLayerDate,
  width,
}) {
  const classes = useStyles()
  const { t } = useTranslation()
  const [map, setMap] = React.useState(null)
  const [data, setData] = React.useState(null)
  const [totalHectares, setTotalHectares] = React.useState(0)
  // Off by default: editing a parcel is about its outline, so the imagery starts clean
  const [showIndexLayer, setShowIndexLayer] = React.useState(false)
  const { loggedUser, user } = useAuth()
  // Index image and legend follow the user's saved palette adjustment for this farm/layer
  // (only in apps that enable palette adjustments)
  const paletteEnabled = usePaletteAdjustmentsEnabled()
  const { data: palette, isLoading: paletteLoading } = usePaletteScript(
    { parcelId: parcel?.id, layerKey: layer?.key, date: selectedLayerDate },
    { enabled: paletteEnabled }
  )
  const { legend } = usePaletteLegend(parcel?.id, layer, { enabled: paletteEnabled })

  const isDeforestationContext =
    user?.profile?.embedded_conf?.only_edit_parcel ||
    loggedUser?.company?.id === 400

  React.useEffect(() => {
    showLayerDate(!!layer && showIndexLayer)
  }, [layer, showIndexLayer, showLayerDate])

  React.useEffect(() => {
    // Initialize the total hectares with the current parcel size
    if (parcel) {
      const area = L.GeometryUtil.geodesicArea(L.GeoJSON.coordsToLatLngs(parcel.geometry.coordinates[0])) / 10000
      setTotalHectares(area)
    }
  }, [parcel])

  React.useEffect(() => {
    if (map) map.fitBounds(computeLatLngBoundsFromGeojson(parcel))
  }, [parcel, map])

  React.useEffect(() => {
    if (farms && selectedResolution) {
      setData({
        type: 'FeatureCollection',
        features: flatMap(
          farms.filter((farm) => farm.checked),
          (farm) =>
            farm.farm_parcels.features.filter((f) =>
              find(f.properties.parcelresolution_set, {
                resolution: selectedResolution.id,
              }) && f.id !== parcel.id
            )
        ),
      })
    }
  }, [farms, selectedResolution])

  return (
    <div className={classes.wrapper}>
      <MapContainer
        center={position}
        minZoom={2}
        maxZoom={20}
        zoom={9}
        style={{ width: '100%', height: 500, borderRadius: 4 }}
        zoomControl={false}
        whenCreated={setMap}
      >
        {!isWidthUp('sm', width) && (
          <TileLayer
            attribution="Maps Hybrid"
            maxZoom={24}
            maxNativeZoom={20}
            url={`https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}`}
          />
        )}

        <MapLayersManager
          companyId={loggedUser?.company?.id}
          map={map}
          width={width}
          openAddParcel={{ open: false }}
          noAppBar
          controlTop={12}
          controlRight={12}
          forceSimpleControl
          initialOverlayVisibility={
            isDeforestationContext
              ? { 'Indigenous Areas': true }
              : undefined
          }
        />

        {layer && showIndexLayer && !paletteLoading && (
          <ImageOverlay
            key={palette?.evalscript_url || 'default'}
            bounds={computeLatLngBoundsFromGeojson(parcel)}
            url={withPaletteScript(
              `${process.env.REACT_APP_BASE_URL_IMAGES}${selectedResolution.instance_id}?${parcel.properties.image_url}&LAYERS=${layer.name}&TIME=${selectedLayerDate}`,
              palette
            )}
            transparent
            interactive
          />
        )}

        {data && farms.find((farm) => farm.checked) && (
          <Parcels
            key={'drawMode' + farms.find((farm) => farm.checked).id}
            parcels={data}
            layersState={layers}
            handleOpenDialog={() => null}
            parcelColor="#808080"
            drawMode
            detectOverlapping={false}
          />
        )}

        <EditParcelGeoman
          farms={farms}
          parcel={parcel}
          dispatchAddedParcels={dispatchAddedParcels}
          parcelGeoJson={parcelGeoJson}
          setParcelGeoJson={setParcelGeoJson}
          editParcel={editParcel}
          setUpdateButtonDisabled={setUpdateButtonDisabled}
          farmsHectares={farmsHectares}
          layer={layer}
          setTotalHectares={setTotalHectares}
        />
        <TotalHectaresControl totalHectares={totalHectares} />
      </MapContainer>

      {layer && (
        <Tooltip title={t(showIndexLayer ? 'Hide index layer' : 'Show index layer')}>
          <IconButton
            onClick={() => setShowIndexLayer((v) => !v)}
            size="small"
            className={`${classes.indexLayerToggle} ${
              showIndexLayer ? classes.indexLayerToggleActive : ''
            }`}
          >
            {showIndexLayer ? (
              <VisibilityIcon className={classes.toggleIcon} />
            ) : (
              <VisibilityOffIcon className={classes.toggleIcon} />
            )}
          </IconButton>
        </Tooltip>
      )}

      {layer && showIndexLayer && layer.legend?.length > 0 && (
        <Legend layersState={layers} layer={{ ...layer, legend }} />
      )}
    </div>
  )
}

export default withWidth()(EditParcelMap)
